import { useReactiveVar } from "@apollo/client";
import { userVar } from "../utils/reactive-variable";
import { usePathname, useRouter } from "next/navigation";
import { centralizedLogout } from "../services/authService";
import { CookieEnum, eraseCookie } from "@/shared/utils/cookie";

export type RegisterType = "login" | "signup";

export type User = {
  token?: string | null;
  id?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  isPhoneVerified?: boolean;
  isEmailVerfied?: boolean;
};

interface AuthHook {
  isLoggedIn: boolean;
  user?: User;
  logout: () => Promise<void>;
  updateUser: (newUserData: User) => void;
}

const useAuth = (): AuthHook => {
  const user = useReactiveVar<User | undefined>(userVar);
  const router = useRouter();
  const pathname = usePathname();

  const updateUser = (newUserData: User) => {
    const updatedUser: User = {
      ...user,
      ...newUserData,
    };

    // Ensure user ID always starts with "CustomerUser_"
    if (updatedUser.id && !updatedUser.id.startsWith("CustomerUser_")) {
      updatedUser.id = `CustomerUser_${updatedUser.id}`;
    }

    userVar(updatedUser);
  };

  const handleLogout = async () => {
    try {
      await centralizedLogout();
    } catch (error) {
      // Continue with local cleanup even if API call fails
      // This is expected when session is already invalid
    }

    eraseCookie(CookieEnum.AUTH_TOKEN, { sameSite: "strict" });
    eraseCookie(CookieEnum.CLIENT_CENTRAL_TOKEN, { sameSite: "strict" });
    localStorage.removeItem(CookieEnum.CLIENT_CENTRAL_TOKEN);
    userVar(undefined);

    if (pathname.includes("/me/")) {
      router.replace("/");
    }
  };

  return {
    isLoggedIn: !!user?.id,
    user,
    logout: handleLogout,
    updateUser,
  };
};

export { useAuth };
